import {
  forwardRef,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type FocusEvent,
  type KeyboardEvent,
  type SelectHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../../lib/utils.js';

import {
  collectSelectOptions,
  foldSelectSearchText,
  type SelectOptionItem,
} from './select-options.js';

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
  /** One-line options with ellipsis; full label on the native tooltip. */
  optionOverflow?: 'truncate';
}

/** Matches `.select-menu { max-height: 220px }` — do not invent a second cap. */
const SELECT_MENU_MAX_HEIGHT_PX = 220;
const SELECT_MENU_GAP_PX = 4;

function clampSelectMenuInset(offset: number, viewportHeight: number): number {
  const maxInset = Math.max(0, viewportHeight - SELECT_MENU_MAX_HEIGHT_PX);
  return Math.min(Math.max(offset, 0), maxInset);
}

function selectMenuViewportStyle(
  rect: Pick<DOMRect, 'top' | 'bottom' | 'left' | 'right' | 'width'>,
  viewport: { width: number; height: number },
): CSSProperties {
  const spaceBelow = viewport.height - rect.bottom;
  const spaceAbove = rect.top;
  // Flip upward only when the max-height menu cannot fit below and more room exists above.
  const openUpward = spaceBelow < SELECT_MENU_MAX_HEIGHT_PX && spaceAbove > spaceBelow;
  const placement = openUpward
    ? {
        bottom: clampSelectMenuInset(
          viewport.height - rect.top + SELECT_MENU_GAP_PX,
          viewport.height,
        ),
      }
    : { top: clampSelectMenuInset(rect.bottom + SELECT_MENU_GAP_PX, viewport.height) };

  const viewportPad = 8;
  const spaceRight = viewport.width - rect.left - viewportPad;
  const spaceLeft = rect.right - viewportPad;
  const growLeft = spaceLeft > spaceRight;
  const maxWidth = Math.max(rect.width, growLeft ? spaceLeft : spaceRight);

  return {
    position: 'fixed',
    ...(growLeft ? { right: viewport.width - rect.right, left: 'auto' } : { left: rect.left }),
    minWidth: rect.width,
    width: 'max-content',
    maxWidth,
    zIndex: 'var(--z-popover)',
    ...placement,
  };
}

function assignSelectValue(element: HTMLSelectElement, nextValue: string): void {
  element.value = nextValue;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    className,
    invalid = false,
    children,
    onChange,
    onBlur,
    value,
    defaultValue,
    disabled = false,
    id,
    name,
    style,
    autoFocus,
    'aria-label': ariaLabel,
    optionOverflow,
    ...props
  },
  forwardedRef,
) {
  const innerRef = useRef<HTMLSelectElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();
  const isControlled = value !== undefined;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const [panelStyle, setPanelStyle] = useState<CSSProperties | undefined>();
  const [uncontrolledValue, setUncontrolledValue] = useState(() =>
    defaultValue === undefined ? '' : String(defaultValue),
  );

  const options = collectSelectOptions(children);
  const currentValue = isControlled
    ? String(Array.isArray(value) ? (value[0] ?? '') : value)
    : uncontrolledValue;
  const selectedOption = options.find((item) => item.value === currentValue);
  const displayLabel = selectedOption !== undefined ? selectedOption.label : '';
  const foldedQuery = foldSelectSearchText(query);
  const filtered =
    foldedQuery.length === 0
      ? options
      : options.filter((item) => foldSelectSearchText(item.label).includes(foldedQuery));
  const safeHighlighted = filtered.length === 0 ? 0 : Math.min(highlighted, filtered.length - 1);

  function setSelectRef(node: HTMLSelectElement | null) {
    innerRef.current = node;
    if (typeof forwardedRef === 'function') {
      forwardedRef(node);
    } else if (forwardedRef !== null) {
      forwardedRef.current = node;
    }
  }

  function closeWithoutCommit() {
    setOpen(false);
    setQuery('');
  }

  function emitChange(nextValue: string) {
    const el = innerRef.current;
    if (el === null || el.value === nextValue) {
      return;
    }
    assignSelectValue(el, nextValue);
    if (!isControlled) {
      setUncontrolledValue(nextValue);
    }
    onChange?.({
      target: el,
      currentTarget: el,
    } as ChangeEvent<HTMLSelectElement>);
  }

  function commitOption(option: SelectOptionItem) {
    if (option.disabled) {
      return;
    }
    emitChange(option.value);
    closeWithoutCommit();
  }

  function openMenu() {
    if (disabled) {
      return;
    }
    const selectedIndex = options.findIndex((item) => item.value === currentValue);
    setHighlighted(selectedIndex < 0 ? 0 : selectedIndex);
    setQuery('');
    setOpen(true);
  }

  function handleNativeChange(event: ChangeEvent<HTMLSelectElement>) {
    if (!isControlled) {
      setUncontrolledValue(event.target.value);
    }
    onChange?.(event);
  }

  function handleInputBlur(event: FocusEvent<HTMLInputElement>) {
    const next = event.relatedTarget;
    if (
      next instanceof Node &&
      (rootRef.current?.contains(next) === true || panelRef.current?.contains(next) === true)
    ) {
      return;
    }
    closeWithoutCommit();
    const el = innerRef.current;
    if (el !== null) {
      onBlur?.({
        ...event,
        target: el,
        currentTarget: el,
      });
    }
  }

  function handleInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) {
        openMenu();
        return;
      }
      setHighlighted((current) => Math.min(current + 1, Math.max(filtered.length - 1, 0)));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        openMenu();
        return;
      }
      setHighlighted((current) => Math.max(current - 1, 0));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (!open) {
        openMenu();
        return;
      }
      const option = filtered[safeHighlighted];
      if (option !== undefined) {
        commitOption(option);
      }
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      closeWithoutCommit();
    }
  }

  useLayoutEffect(() => {
    if (isControlled) {
      return;
    }
    const el = innerRef.current;
    if (el === null) {
      return;
    }
    setUncontrolledValue(el.value);
  }, [isControlled]);

  useLayoutEffect(() => {
    if (!open || rootRef.current === null) {
      return;
    }
    function updatePosition() {
      const trigger = rootRef.current;
      if (trigger === null) {
        return;
      }
      const rect = trigger.getBoundingClientRect();
      setPanelStyle(
        selectMenuViewportStyle(rect, { width: window.innerWidth, height: window.innerHeight }),
      );
    }
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    function handlePointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (
        rootRef.current?.contains(target) === true ||
        panelRef.current?.contains(target) === true
      ) {
        return;
      }
      closeWithoutCommit();
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [open]);

  const panel =
    open && panelStyle !== undefined ? (
      <div
        ref={panelRef}
        id={listboxId}
        className={cn('menu select-menu', optionOverflow === 'truncate' && 'select-menu--truncate')}
        role="listbox"
        style={panelStyle}
        onMouseDown={(event) => {
          event.preventDefault();
        }}
      >
        {filtered.length === 0 ? (
          <div className="select-empty">Nada encontrado</div>
        ) : (
          filtered.map((option, index) => (
            <button
              key={`${option.value}-${index}`}
              id={`${listboxId}-opt-${index}`}
              type="button"
              role="option"
              disabled={option.disabled}
              aria-selected={index === safeHighlighted}
              className={cn('menu-item', index === safeHighlighted && 'is-active')}
              title={optionOverflow === 'truncate' ? option.label : undefined}
              onMouseEnter={() => {
                setHighlighted(index);
              }}
              onClick={() => {
                commitOption(option);
              }}
            >
              {option.label}
            </button>
          ))
        )}
      </div>
    ) : null;

  return (
    <div ref={rootRef} className={cn('select-host', className)} style={style}>
      <select
        {...props}
        ref={setSelectRef}
        id={id}
        name={name}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-hidden={open || undefined}
        role={open ? undefined : 'listbox'}
        tabIndex={-1}
        className="select-native"
        value={isControlled ? value : undefined}
        defaultValue={isControlled ? undefined : defaultValue}
        onChange={handleNativeChange}
      >
        {children}
      </select>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        disabled={disabled}
        autoFocus={autoFocus}
        autoComplete="off"
        spellCheck={false}
        aria-invalid={invalid || undefined}
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          open && filtered.length > 0 ? `${listboxId}-opt-${safeHighlighted}` : undefined
        }
        className={cn('select', invalid && 'is-invalid', className)}
        value={open ? query : displayLabel}
        onChange={(event) => {
          setQuery(event.target.value);
          setHighlighted(0);
          if (!open) {
            setOpen(true);
          }
        }}
        onFocus={() => {
          if (!open) {
            openMenu();
          }
        }}
        onMouseDown={() => {
          // After a choice the field stays focused, so the next press does not fire focus again.
          if (document.activeElement === inputRef.current && !open) {
            openMenu();
          }
        }}
        onBlur={handleInputBlur}
        onKeyDown={handleInputKeyDown}
      />
      {typeof document !== 'undefined' && panel !== null
        ? createPortal(panel, document.body)
        : null}
    </div>
  );
});
